import {
  ORDER_MODIFICATION_SOURCE_CASHBACK,
  ORDER_MODIFICATION_SOURCE_COUPON,
  ORDER_MODIFICATION_TARGET_DELIVERY_FEE,
  ORDER_MODIFICATION_TARGET_ITEM,
  ORDER_MODIFICATION_TARGET_ITEMS,
  ORDER_MODIFICATION_TARGET_ITEM_PRODUCT,
  ORDER_MODIFICATION_TARGET_ITEM_SECTION,
  ORDER_MODIFICATION_TARGET_ITEM_SECTION_CATEGORY,
  ORDER_MODIFICATION_TARGET_ORDER,
} from '@/constants'
import { calculate, fixFloat, uuid } from '@/helpers'
import {
  getOrderItemModifications,
  normalizeOrderItems,
} from '@/utils/Company/Order/Item'

export async function sendOrder(order) {
  return app.callApi({
    method: 'POST',
    version: 1,
    action: 'orders',
    data: order,
  })
}

/**
 * Calculate the total value of modifications for a given order item.
 *
 * @param {Object} orderItem - The order item object containing products and section information.
 * @param {Array} modifications - An array of modifications to be applied to the order item.
 * @return {Number} The total value of modifications applied to the order item.
 */
export function getTotalOrderItemModifications({
  orderItem,
  modifications = [],
}) {
  const orderItemModifications = getOrderItemModifications({
    orderItem,
    modifications,
  })

  let totalItemModifications = orderItemModifications.reduce(
    (acc, { originalValue: value, metadata = {} }) => {
      const useBasePrice = metadata?.useBasePrice ?? true
      const baseModification = useBasePrice
        ? orderItem.totalBasePrice
        : orderItem.totalPrice
      const isNegative = value.toString().charAt(0) === '-'

      if (isNegative) value = value.toString().substring(1)

      value = calculate(baseModification, value)

      if (isNegative) value *= -1

      return acc + value
    },
    0,
  )

  totalItemModifications = fixFloat(totalItemModifications)

  return totalItemModifications
}

/**
 * Normalizes the order by applying various transformations and calculations.
 *
 * @param {Object} order - The order object to be normalized.
 * @return {Promise<Object>} - The normalized order.
 */
export function normalizeOrder(order) {
  order = {
    id: undefined,
    orderId: uuid(),
    observations: undefined,
    coupon: undefined,
    deliveryAddress: undefined,
    deliveryWay: undefined,
    paymentForm: undefined,
    onlinePayment: undefined,
    phoneNumber: undefined,
    moneyChange: undefined,
    items: [],
    modifications: [],
    rewards: [],
    cashback: {
      discount: 0,
    },
    ...order,
  }

  order = {
    ...order,
    items: normalizeOrderItems(order),

    get totalDeliveryFeeModifications() {
      const deliveryFeeModifications = this.getModificationsByTarget(
        ORDER_MODIFICATION_TARGET_DELIVERY_FEE,
      )

      return (
        deliveryFeeModifications.reduce((acc, { value }) => acc + value, 0) || 0
      )
    },

    get deliveryFee() {
      const { originalDeliveryFee, totalDeliveryFeeModifications } = this

      let deliveryFee = originalDeliveryFee + totalDeliveryFeeModifications

      deliveryFee = fixFloat(deliveryFee)

      return deliveryFee
    },
    getModificationsByTarget(modificationTarget) {
      const { modifications } = this
      const isArrayTarget = Array.isArray(modificationTarget)

      return modifications.filter(({ target }) =>
        isArrayTarget
          ? modificationTarget.includes(target)
          : target === modificationTarget,
      )
    },
    addReward(reward) {
      return this.rewards.push(reward)
    },
    get totalItemsOriginalPrice() {
      let totalItemsOriginalPrice = this.items.reduce(
        (acc, { totalOriginalPrice }) => acc + totalOriginalPrice,
        0,
      )

      totalItemsOriginalPrice = fixFloat(totalItemsOriginalPrice)

      return totalItemsOriginalPrice
    },

    get totalItemsBasePrice() {
      let totalItemsBasePrice = this.items.reduce(
        (acc, { totalBasePrice }) => acc + totalBasePrice,
        0,
      )

      totalItemsBasePrice = fixFloat(totalItemsBasePrice)

      return totalItemsBasePrice
    },
    get discounts() {
      return this.modifications.filter(({ value }) => value < 0)
    },
    get totalDiscounts() {
      let totalDiscounts =
        this.discounts.reduce((acc, { value }) => acc + value, 0) * -1

      totalDiscounts = fixFloat(totalDiscounts)

      return totalDiscounts
    },
    get additions() {
      return this.modifications.filter(({ value }) => value > 0)
    },
    get totalAdditions() {
      let totalAdditions = this.additions.reduce(
        (acc, { value }) => acc + value,
        0,
      )

      totalAdditions = fixFloat(totalAdditions)

      return totalAdditions
    },
    get totalModificationsValue() {
      let totalModificationsValue = this.modifications.reduce(
        (acc, { value }) => acc + value,
        0,
      )

      totalModificationsValue = fixFloat(totalModificationsValue)

      return totalModificationsValue
    },
    get paidPrice() {
      return this.totalPrice
    },
    addModification(modification) {
      const order = this
      const {
        totalPrice,
        totalItemsPrice,
        totalItemsBasePrice,
        deliveryFee,
        modifications,
      } = order
      const { target, targetId, metadata, source, title } = modification

      let { items } = order
      let { value } = modification
      let baseValue = totalPrice

      modification.originalValue = value

      if (!title)
        modification.title =
          source === ORDER_MODIFICATION_SOURCE_COUPON ? 'Cupom' : 'Regra'

      switch (target) {
        case ORDER_MODIFICATION_TARGET_ITEM:
          const orderItem = items.find(({ id }) => id === targetId)

          if (orderItem)
            value = getTotalOrderItemModifications({
              orderItem,
              modifications: [modification],
            })

          break

        case ORDER_MODIFICATION_TARGET_ITEM_PRODUCT:
          items = items.filter(({ products }) =>
            products.some(({ id }) => id === targetId),
          )
          value = this.getTotalItemsModifications({
            items,
            modifications: [modification],
          })
          break

        case ORDER_MODIFICATION_TARGET_ITEM_SECTION:
          items = items.filter(({ section }) => section.id === targetId)
          value = this.getTotalItemsModifications({
            items,
            modifications: [modification],
          })
          break

        case ORDER_MODIFICATION_TARGET_ITEM_SECTION_CATEGORY:
          items = items.filter(({ products }) =>
            products.some(({ category }) => category?.id),
          )
          value = this.getTotalItemsModifications({
            items,
            modifications: [modification],
          })
          break

        case ORDER_MODIFICATION_TARGET_ITEMS:
          let useBasePrice = metadata?.useBasePrice ?? true

          if (source === ORDER_MODIFICATION_SOURCE_CASHBACK)
            useBasePrice = false

          baseValue = useBasePrice ? totalItemsBasePrice : totalItemsPrice
          value = calculate(baseValue, value)
          value = (baseValue - value) * -1
          break

        case ORDER_MODIFICATION_TARGET_DELIVERY_FEE:
          baseValue = deliveryFee
          value = calculate(baseValue, value)
          value = (baseValue - value) * -1

          break

        case ORDER_MODIFICATION_TARGET_ORDER:
          baseValue = totalPrice
          value = calculate(baseValue, value)
          value = (baseValue - value) * -1
          break
      }

      value = fixFloat(value)
      modification.value = value

      modifications.push(modification)

      return modification
    },
    getTotalItemsModifications({ items, modifications, order }) {
      modifications = modifications ?? order?.modifications ?? []

      let totalItemsModifications = items.reduce(
        (acc, orderItem) =>
          acc + getTotalOrderItemModifications({ orderItem, modifications }),
        0,
      )

      totalItemsModifications = fixFloat(totalItemsModifications)

      return totalItemsModifications
    },

    get totalOriginalPrice() {
      const { totalItemsOriginalPrice, originalDeliveryFee = 0 } = this

      let totalOriginalPrice = 0

      totalOriginalPrice += totalItemsOriginalPrice
      totalOriginalPrice += originalDeliveryFee
      totalOriginalPrice = fixFloat(totalOriginalPrice)

      return totalOriginalPrice
    },

    get totalPrice() {
      const { totalOriginalPrice, totalModificationsValue } = this

      let totalPrice = 0

      totalPrice += totalOriginalPrice
      totalPrice += totalModificationsValue
      totalPrice = fixFloat(totalPrice)

      return totalPrice
    },

    get totalItemsPrice() {
      let totalItemsPrice = this.items.reduce(
        (acc, { totalPrice }) => acc + totalPrice,
        0,
      )

      totalItemsPrice = fixFloat(totalItemsPrice)

      return totalItemsPrice
    },

    get originalDeliveryFee() {
      const order = this
      const { deliveryWay, deliveryAddress } = order

      let deliveryFee = deliveryWay?.basePrice ?? 0

      if (deliveryWay) {
        let modifier = deliveryWay.totalPrice ?? deliveryWay.price

        if (deliveryWay.requireAddress === true && deliveryAddress) {
          let { city, neighborhood } = deliveryAddress

          if (city) {
            const cities =
              deliveryWay?.cities ?? deliveryWay?.allowedCities ?? []

            city = cities.find(({ id }) => id === city.id)

            if (!city || city?.companyDeliveryWayCity?.active === false) return

            modifier =
              city?.companyDeliveryWayCity?.deliveryPrice ??
              city?.companyDeliveryWayCity?.price
          }

          if (neighborhood) {
            const neighborhoods = deliveryWay?.neighborhoods ?? []

            neighborhood = neighborhoods.find(
              ({ id }) => id === neighborhood.id,
            )

            if (neighborhood?.companyDeliveryWayNeighborhood?.active === false)
              return

            modifier =
              neighborhood?.companyDeliveryWayNeighborhood?.deliveryPrice ??
              neighborhood?.companyDeliveryWayNeighborhood?.price
          }
        }

        if (modifier !== undefined)
          deliveryFee = calculate(deliveryFee, modifier)
      }

      deliveryFee = fixFloat(deliveryFee)

      return deliveryFee
    },
  }

  return order
}
